import { rgba } from 'polished';
import styled, { css } from 'styled-components';

export const Card = styled.div`
  background: ${({ theme }) => theme.colors.WHITE};
  box-shadow: 0 4px 12px ${({ theme }) => rgba(theme.colors.BLACK, 0.1)};
  border-radius: 3px;
  color: ${({ theme }) => theme.colors.BLACK};
  padding: 15px;
  overflow: hidden;
  min-width: 0;
`;

export const OutlinedButton = styled.div<{
  $active?: boolean;
  $disabled?: boolean;
  $minimal?: boolean;
  $small?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 1px;
  padding: 0 10px;
  height: 30px;
  gap: 6px;
  background-color: ${({ theme }) => rgba(theme.highlightColor, 0.2)};
  box-shadow: inset 0 0 0 1px ${({ theme }) => rgba(theme.highlightColor, 0.4)};
  cursor: pointer;
  transition: background-color ${({ theme }) => theme.animation.DURATION}
    ${({ theme }) => theme.animation.EASING};

  &:hover {
    background-color: ${({ theme }) => rgba(theme.highlightColor, 0.4)};
  }

  &:active {
    background-color: ${({ theme }) => rgba(theme.highlightColor, 0.5)};
  }

  ${({ $small }) =>
    $small &&
    css`
      height: 24px;
      gap: 6px;
      padding: 0 5px;
    `}

  ${({ $minimal }) =>
    $minimal &&
    css`
      background-color: transparent;
      box-shadow: none;

      &:hover {
        background-color: ${({ theme }) => rgba(theme.highlightColor, 0.2)};
      }

      &:active {
        background-color: ${({ theme }) => rgba(theme.highlightColor, 0.4)};
      }
    `}

  ${({ $active, theme }) =>
    $active &&
    css`
      background-color: ${rgba(theme.highlightColor, 0.2)};
    `}

    ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.4;
      pointer-events: none;
    `}
`;

export const MinimalButton = styled.div<{
  $selected?: boolean;
  $small?: boolean;
}>`
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border-radius: ${({ theme }) => theme.borderRadius};
  padding: 0 10px;
  height: 28px;
  color: ${({ theme }) => theme.appUI.fontColor.DEFAULT};
  line-height: 20px;
  cursor: pointer;
  transition:
    background-color ${({ theme }) => theme.animation.DURATION}
      ${({ theme }) => theme.animation.EASING},
    color ${({ theme }) => theme.animation.DURATION}
      ${({ theme }) => theme.animation.EASING};

  svg {
    color: ${({ theme }) => theme.appUI.fontColor.MUTED};
  }

  &:hover {
    background-color: ${({ theme }) => rgba(theme.colors.BLACK, 0.05)};
    color: ${({ theme }) => theme.colors.BLACK};
  }

  ${({ $selected, theme }) =>
    $selected &&
    css`
      background-color: ${rgba(theme.colors.BLACK, 0.1)};
      color: ${theme.colors.BLACK};

      &:hover {
        background-color: ${rgba(theme.colors.BLACK, 0.1)};
      }
    `};

  ${({ $small, theme }) =>
    $small &&
    css`
      padding: 0 5px;
      height: 20px;
      color: ${theme.appUI.fontColor.MUTED};
    `};
`;

export const SimpleButton = styled.div<{
  $isVar?: boolean;
  $noPadding?: boolean;
}>`
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  justify-content: space-between;
  gap: 3px;
  height: 24px;
  padding: 0 7px;
  line-height: initial;
  font-size: ${({ theme }) => theme.fontSize.SMALL};
  color: ${({ theme }) => theme.appUI.fontColor.MUTED};
  box-shadow:
    0 0 0 1px ${({ theme }) => theme.appUI.borderColor.DEFAULT},
    0 1px 2px 1px ${({ theme }) => rgba(theme.appUI.borderColor.DEFAULT, 0.3)};
  border-radius: 1px;

  svg {
    color: ${({ theme }) => theme.appUI.fontColor.MUTED};
  }

  ${({ $isVar, theme }) =>
    $isVar &&
    css`
      font-family: ${theme.fontFamily.MONO};
      font-size: 11px;
    `}

  ${({ $noPadding }) =>
    $noPadding &&
    css`
      padding-left: 3px;
    `}
`;

export const Pill = styled.div<{ $color?: string; $lightBkg: boolean }>`
  border-radius: 2px;
  background: ${({ theme }) => rgba(theme.colors.LIGHT_GREEN, 0.2)};
  color: ${({ $lightBkg, theme }) =>
    $lightBkg ? theme.colors.DARK_GREEN : theme.colors.LIGHT_GREEN};
  padding: 2px 4px;
  font-size: ${({ theme }) => theme.fontSize.SMALL};
  line-height: 14px;
  font-family: ${({ theme }) => theme.fontFamily.MONO};
  font-size: 11px;

  ${({ $color, $lightBkg, theme }) =>
    $color === 'blue' &&
    css`
      background: ${rgba(theme.colors.LIGHT_BLUE, 0.2)};
      color: ${$lightBkg ? theme.colors.DARK_BLUE : theme.colors.LIGHT_BLUE};
    `}
`;
